import dayjs from 'dayjs';
import { MetaAdsCampaign } from '../components/apis/metaAdsApi';

export interface CampaignVideo {
  id: string;
  title: string;
}

/**
 * Format a money amount with its currency code, e.g. formatMoney(55, "PLN") => "PLN 55.00".
 * Currency is the ad-account currency (ISO code); defaults to "USD" when unknown.
 */
export const formatMoney = (amount: number, currency?: string): string =>
  `${currency || "USD"} ${amount.toFixed(2)}`;

export interface CampaignCardData {
  id: string;
  name: string;
  status: "Active" | "Paused" | "Draft";
  conversionPage: string;
  startDate: string;
  endDate: string;
  rawStartDate?: string | null;
  rawEndDate?: string | null;
  isScheduled?: boolean;
  budget: string;
  currency?: string; // Ad-account currency code (e.g. "PLN") for the budget display
  engagement: string;
  visits: string;
  clicks: string;
  cost: string;
  results: {
    clicks: string;
    ctr: string;
    conversions: string;
  };
  lastUpdated: string;
  // Additional fields for campaigns from Meta
  isFromMeta?: boolean;
  isDraft?: boolean;
  objective?: string;
  promoteType?: "track" | "playlist";
  accountId?: string;
  createdAt?: string;
  updatedAt?: string;
  linkUrls?: string[];
  videos?: CampaignVideo[];
  trackInfo?: {
    linkUrl?: string;
    smartLink?: string;
    artistName?: string;
    songName?: string;
  };
  performance?: {
    dateRange: string;
    reach: number;
    impressions: number;
    clicks: number;
    spend: number;
    conversions: number;
  };
}

export function transformMetaCampaignToCard(
  campaign: MetaAdsCampaign
): CampaignCardData {
  // Format status
  const status =
    campaign.status === "ACTIVE"
      ? ("Active" as const)
      : campaign.status === "PAUSED"
      ? ("Paused" as const)
      : ("Active" as const);

  // Format dates
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    const { type, amount } = campaign.budget;

    if (!amount) return "Not set";

    // For DAILY budget, show amount/day
    // For SCHEDULED budget, also show amount/day
    const dailyStr = `€${amount}/day`;

    // Estimate monthly budget (30 days)
    const total = amount * 30;
    const totalStr = `€${total} total`;

    if (type === "SCHEDULED") {
      return `${totalStr} (${dailyStr}) - Scheduled`;
    } else {
      return `${totalStr} (${dailyStr}) - Always Active`;
    }
  };

  // Safely access schedule properties
  const schedule = campaign.schedule || {
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    timezone: null,
    isScheduled: false,
  };

  // Format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Format last updated
  const formatLastUpdated = (date?: string) => {
    if (!date) return "Today, 14:32"; // Default fallback
    const now = dayjs();
    const updated = dayjs(date);
    const diffHours = now.diff(updated, 'hour');

    if (diffHours < 24) {
      return `Today, ${updated.format("HH:mm")}`;
    } else if (diffHours < 48) {
      return "Yesterday";
    } else {
      return updated.format("DD MMM YYYY");
    }
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return {
    id: campaign._id,
    name: campaign.campaignName,
    status,
    conversionPage: "Smart link / Landing page",
    startDate: formatDate(schedule.startDate),
    endDate: formatDate(schedule.endDate),
    budget: getBudgetString(),
    engagement: campaign.performance.ctr
      ? formatPercentage(campaign.performance.ctr)
      : "N/A",
    visits: formatNumber(campaign.performance.impressions),
    clicks: formatNumber(campaign.performance.clicks),
    cost: campaign.performance.cpc
      ? `€${campaign.performance.cpc.toFixed(2)}`
      : "N/A",
    results: {
      clicks: `${formatNumber(campaign.performance.clicks)} clicks`,
      ctr: campaign.performance.ctr
        ? `${formatPercentage(campaign.performance.ctr)} CTR`
        : "N/A",
      conversions: "N/A", // Not available in backend yet
    },
    lastUpdated: formatLastUpdated(
      campaign.performance.lastUpdated || campaign.updatedAt
    ),
  };
}
